/**
 * Client Service for API communication with backend
 */

import { auth } from '../config/firebase';
import { logger } from '../utils/logger';
import type { EmailSearchResult } from '../types/confirmation';

// API Response types
interface APIResponse<T> {
  success: boolean;
  data: T;
  message: string;
  error_code?: string;
}

// Trade-related types
export interface UnmatchedTrade {
  id: string;
  tradeNumber: string;
  counterpartyName: string;
  productType: string;
  tradeDate: string;
  valueDate: string;
  direction: string;
  currency1: string;
  quantityCurrency1: number;
  currency2: string;
  forwardPrice: number;
  maturityDate: string;
  fixingReference: string;
  settlementType: string;
  settlementCurrency: string;
  paymentDate: string;
  status: string;
  createdAt: string;
  ContractUrl?: string;
  ContractUploadedAt?: string;
  ContractUploadedBy?: string;
}

export interface EmailConfirmation {
  id: string;
  matchId?: string;  // Optional - only present for matched emails
  emailSender: string;
  emailDate: string;
  emailTime: string;
  emailSubject: string;
  emailBody: string;
  bankTradeNumber: string;
  llmExtractedData?: any;
  status: string;
  createdAt: string;
}

export interface TradeMatch {
  id: string;
  matchId: string;  // Unique identifier for linking grids
  tradeId: string;
  emailId: string;
  confidenceScore: number;
  matchReasons: string[];
  discrepancies: any[];
  status: string;
  createdAt: string;
}

export interface UploadResult {
  upload_session_id: string;
  file_name: string;
  file_size: number;
  records_processed: number;
  message: string;
  // Additional fields for email uploads
  email_id?: string;
  trades_extracted?: number;
  confirmation_detected?: boolean;
  matches_found?: number;
  duplicates_found?: number;
  counterparty_name?: string;
  matched_trade_numbers?: string[];
}

export interface ProcessMatchesResult {
  unmatched_trades: number;
  unmatched_emails: number;
  matches_found: number;
  confidence_scores: number[];
  message: string;
}

export interface CounterpartyExpiry {
  id: string;
  TradeNumber: string;  // Our trade number (for lookup)
  CounterpartyTradeNumber: string;  // Their trade number
  OurParty: string;
  CounterpartyName: string;
  MaturityDate: string;
  PaymentDate?: string;
  Direction: string;
  Currency1: string;
  QuantityCurrency1: number;
  Currency2: string;
  QuantityCurrency2?: number;
  Price: number;
  SettlementType: string;
  SettlementCurrency: string;
  FixingOffset: string;
  FixingReference: string;
  FixingPrice?: number;
  SettlementPrice?: number;
  // Settlement amounts (received from counterparty)
  WePayCurrency?: string;
  WePayAmount?: number;
  WeReceiveCurrency?: string;
  WeReceiveAmount?: number;
  ProductType: string;
  createdAt?: string;
  updatedAt?: string;
}

// Confirmation Comparison Fields Settings types
export interface CounterpartyComparisonFields {
  spot: string[];
  forward: string[];
}

export interface ConfirmationComparisonFieldsSettings {
  counterpartyFields: Record<string, CounterpartyComparisonFields>;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

class ClientService {
  private baseURL: string;

  constructor() {
    // Use environment variable for API URL, matching APIClient pattern
    const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    this.baseURL = `${apiUrl}/api/v1/clients`;
    logger.log('[ClientService] Using API base URL:', this.baseURL);
  }

  /**
   * Get authentication token from Firebase
   */
  private async getAuthToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  /**
   * Make authenticated API request
   */
  private async makeRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const token = await this.getAuthToken();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.detail || errorMessage;
      } catch {
        // Use default error message if JSON parsing fails
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  // ========== Trade Management Methods ==========

  /**
   * Get all unmatched trades for a client
   */
  async getUnmatchedTrades(
    clientId: string,
    includeInternal: boolean = false,
    dateFrom?: string,
    dateTo?: string,
    includeSplits: boolean = false,
  ): Promise<UnmatchedTrade[]> {
    const params = new URLSearchParams();
    if (includeInternal) params.set('include_internal', 'true');
    if (includeSplits) params.set('include_splits', 'true');
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const qs = params.toString();
    const url = `${this.baseURL}/${clientId}/unmatched-trades${qs ? `?${qs}` : ''}`;
    const response = await this.makeRequest<UnmatchedTrade[]>(url);
    return response.data;
  }

  /**
   * Get Forward trades only for a client (for Contract Comparison)
   */
  async getForwardTrades(
    clientId: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<UnmatchedTrade[]> {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const qs = params.toString();
    const response = await this.makeRequest<UnmatchedTrade[]>(
      `${this.baseURL}/${clientId}/forward-trades${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  /**
   * Get commodity trades for a client (KAN-256)
   */
  async getCommodityTrades(clientId: string, dateFrom?: string, dateTo?: string, includeInternal = false): Promise<any[]> {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (includeInternal) params.set('include_internal', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.makeRequest<any[]>(
      `${this.baseURL}/${clientId}/commodity-trades${qs}`
    );
    return response.data;
  }

  /**
   * Upload contract PDF for a trade
   */
  async uploadTradeContract(clientId: string, tradeId: string, file: File): Promise<any> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseURL}/${clientId}/trades/${tradeId}/contract`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || error.detail || 'Failed to upload contract');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Delete contract for a trade
   */
  async deleteTradeContract(clientId: string, tradeId: string): Promise<void> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseURL}/${clientId}/trades/${tradeId}/contract`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Delete failed' }));
      throw new Error(error.message || error.detail || 'Failed to delete contract');
    }
  }

  /**
   * Get a signed URL for viewing a trade's contract
   * Signed URLs are valid for 1 hour
   *
   * Optimized: Accepts contract_url directly to avoid database lookup (saves 3-5 seconds)
   */
  async getContractSignedUrl(clientId: string, contractUrl: string): Promise<string> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    // Pass contract_url as query parameter to avoid Firestore lookup
    const encodedUrl = encodeURIComponent(contractUrl);
    const response = await fetch(`${this.baseURL}/${clientId}/contract/signed-url?contract_url=${encodedUrl}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get signed URL' }));
      throw new Error(error.message || error.detail || 'Failed to get signed URL');
    }

    const result = await response.json();
    return result.data.signed_url;
  }

  /**
   * Send a trade's contract for electronic signature via SignAPIs.
   * If ruleId is provided, the backend uses the rule's signatories and flow.
   */
  async sendForSignature(clientId: string, tradeId: string, ruleId?: string): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/trades/${tradeId}/send-for-signature`,
      {
        method: 'POST',
        body: JSON.stringify({ rule_id: ruleId || undefined }),
      }
    );
    return response.data;
  }

  /**
   * Sign a document via API-direct signing (no email flow).
   * The logged-in user must be a pending signatory on the trade.
   */
  async signDocument(
    clientId: string,
    tradeId: string,
    signatureText: string,
    geolocation?: string,
    geolocationReference?: string,
    ruleId?: string,
  ): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/trades/${tradeId}/sign`,
      {
        method: 'POST',
        body: JSON.stringify({
          signature_text: signatureText,
          geolocation: geolocation || undefined,
          geolocation_reference: geolocationReference || undefined,
          rule_id: ruleId || undefined,
        }),
      }
    );
    return response.data;
  }

  /**
   * Sign an already-submitted document via API.
   * Used when the document was sent for signature and it's now the user's turn.
   */
  async signExistingDocument(
    clientId: string,
    tradeId: string,
    signatureText: string,
    geolocation?: string,
    geolocationReference?: string,
  ): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/trades/${tradeId}/sign-existing`,
      {
        method: 'POST',
        body: JSON.stringify({
          signature_text: signatureText,
          geolocation: geolocation || undefined,
          geolocation_reference: geolocationReference || undefined,
        }),
      }
    );
    return response.data;
  }

  /**
   * Send multiple trade contracts for batch signature via email.
   */
  async batchSign(clientId: string, tradeIds: string[], ruleId: string): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/batch-sign`,
      {
        method: 'POST',
        body: JSON.stringify({ trade_ids: tradeIds, rule_id: ruleId }),
      }
    );
    return response.data;
  }

  /**
   * Create a batch and sign all documents directly via API.
   */
  async batchSignDirect(
    clientId: string,
    tradeIds: string[],
    ruleId: string,
    signatureText: string,
    geolocation?: string,
    geolocationReference?: string,
  ): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/batch-sign-direct`,
      {
        method: 'POST',
        body: JSON.stringify({
          trade_ids: tradeIds,
          rule_id: ruleId,
          signature_text: signatureText,
          geolocation: geolocation || undefined,
          geolocation_reference: geolocationReference || undefined,
        }),
      }
    );
    return response.data;
  }

  /**
   * Get all email confirmations for a client
   */
  async getEmailConfirmations(clientId: string): Promise<EmailConfirmation[]> {
    const response = await this.makeRequest<EmailConfirmation[]>(
      `${this.baseURL}/${clientId}/email-confirmations`
    );
    return response.data;
  }

  /**
   * Get all trade matches for a client
   */
  async getTradeMatches(clientId: string): Promise<TradeMatch[]> {
    const response = await this.makeRequest<TradeMatch[]>(
      `${this.baseURL}/${clientId}/matches`
    );
    return response.data;
  }

  /**
   * Get all email confirmations with extracted trade data
   */
  async getAllEmailConfirmations(clientId: string): Promise<any[]> {
    const response = await this.makeRequest<any[]>(
      `${this.baseURL}/${clientId}/all-email-confirmations`
    );
    return response.data;
  }

  /**
   * Upload trade file (Excel/CSV)
   */
  async uploadTradeFile(clientId: string, file: File): Promise<UploadResult> {
    return this.uploadTradeFileWithOverwrite(clientId, file, false);
  }

  /**
   * Upload trade file with overwrite option
   */
  async uploadTradeFileWithOverwrite(clientId: string, file: File, overwrite: boolean = false): Promise<UploadResult> {
    const token = await this.getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('overwrite', overwrite.toString());

    const response = await fetch(`${this.baseURL}/${clientId}/upload-trades`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.detail || errorMessage;
      } catch {
        // Use default error message
      }
      
      throw new Error(errorMessage);
    }

    const result: APIResponse<UploadResult> = await response.json();
    return result.data;
  }

  /**
   * Upload email file (.msg/.pdf)
   */
  async uploadEmailFile(clientId: string, file: File): Promise<UploadResult> {
    const token = await this.getAuthToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseURL}/${clientId}/upload-emails`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.detail || errorMessage;
      } catch {
        // Use default error message
      }
      
      throw new Error(errorMessage);
    }

    const result: APIResponse<UploadResult> = await response.json();
    return result.data;
  }

  /**
   * Delete all unmatched trades for a client
   */
  async deleteAllUnmatchedTrades(clientId: string): Promise<{trades_deleted: number, message: string}> {
    const response = await this.makeRequest<{trades_deleted: number, message: string}>(
      `${this.baseURL}/${clientId}/unmatched-trades`,
      { method: 'DELETE' }
    );
    return response.data;
  }

  /**
   * Process matches between trades and emails
   */
  async processMatches(clientId: string): Promise<ProcessMatchesResult> {
    const response = await this.makeRequest<ProcessMatchesResult>(
      `${this.baseURL}/${clientId}/process-matches`,
      { method: 'POST' }
    );
    return response.data;
  }

  /**
   * Update email confirmation status
   * @param clientId - The client ID
   * @param emailId - The email confirmation ID
   * @param status - The new status
   * @returns Updated email confirmation
   */
  async updateEmailConfirmationStatus(
    clientId: string,
    emailId: string,
    status: string
  ): Promise<EmailConfirmation> {
    // TODO: Add audit fields (updatedBy, updatedAt) when centralized audit system is implemented
    const response = await this.makeRequest<EmailConfirmation>(
      `${this.baseURL}/${clientId}/email-confirmations/${emailId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          status,
          // Placeholder for audit fields
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email || 'unknown'
        })
      }
    );
    return response.data;
  }

  /**
   * Update trade status
   * @param clientId - The client ID
   * @param tradeId - The trade ID
   * @param status - The new status (e.g., 'matched', 'unmatched', 'confirmed_via_portal', 'disputed')
   * @returns Updated trade data
   */
  async updateTradeStatus(
    clientId: string,
    tradeId: string,
    status: string
  ): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/trades/${tradeId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          status,
          // Audit fields
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email || 'unknown'
        })
      }
    );
    return response.data;
  }

  // ========== Utility Methods ==========

  /**
   * Format currency amount for display
   */
  static formatCurrency(amount: number, locale: string = 'es-CL'): string {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string, locale: string = 'es-CL'): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(locale);
    } catch {
      return dateString;
    }
  }

  /**
   * Get status display class for styling
   */
  static getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'unmatched':
        return 'status-unmatched';
      case 'matched':
        return 'status-matched';
      case 'confirmed':
        return 'status-confirmed';
      case 'review_needed':
        return 'status-review-needed';
      default:
        return 'status-default';
    }
  }

  /**
   * Generate settlement instruction document for a trade
   */
  async generateSettlementInstruction(
    clientId: string,
    tradeNumber: string,
    bankTradeNumber?: string,
    emailId?: string,
    cargarAccountId?: string,
    abonarAccountId?: string,
    trigger?: string
  ): Promise<APIResponse<any>> {
    try {
      const token = await this.getAuthToken();

      // Create FormData for the POST request
      const formData = new FormData();
      formData.append('trade_number', tradeNumber);
      if (bankTradeNumber) {
        formData.append('bank_trade_number', bankTradeNumber);
      }
      if (emailId) {
        formData.append('email_id', emailId);
      }
      if (cargarAccountId) {
        formData.append('cargar_account_id', cargarAccountId);
      }
      if (abonarAccountId) {
        formData.append('abonar_account_id', abonarAccountId);
      }
      if (trigger) {
        formData.append('trigger', trigger);
      }
      
      const response = await fetch(`${this.baseURL}/${clientId}/settlement-instructions/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        body: formData
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Extract user-friendly error message from API response (FastAPI uses 'detail' field)
        const errorMessage = data.detail || data.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      logger.error('Error generating settlement instruction:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to generate settlement instruction'
      };
    }
  }

  /**
   * Preview which bank accounts the rules engine resolves for each trade,
   * plus all active bank accounts for dropdown population.
   */
  async previewSettlementAccounts(
    clientId: string,
    tradeNumbers: string[]
  ): Promise<APIResponse<{
    previews: Array<{
      tradeNumber: string;
      ruleMatched: boolean;
      ruleName?: string;
      cargarAccountId?: string;
      abonarAccountId?: string;
      templateId?: string;
      outputFormat?: string;
      error?: string;
    }>;
    bankAccounts: Array<{
      id: string;
      accountName: string;
      bankName: string;
      swiftCode: string;
      accountCurrency: string;
      accountNumber: string;
      isDefault: boolean;
    }>;
  }>> {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${this.baseURL}/${clientId}/settlement-instructions/preview-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ trade_numbers: tradeNumbers }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      logger.error('Error previewing settlement accounts:', error);
      return {
        success: false,
        data: null as any,
        message: error instanceof Error ? error.message : 'Failed to preview settlement accounts'
      };
    }
  }

  /**
   * Get a fresh signed URL for accessing a settlement instruction document
   * @param clientId - The client ID
   * @param emailId - Optional email ID (legacy flow - looks up path from email document)
   * @param storagePath - Optional storage path (preferred - uses path directly from trade document)
   */
  async getSettlementInstructionUrl(clientId: string, emailId?: string, storagePath?: string): Promise<APIResponse<any>> {
    try {
      const token = await this.getAuthToken();

      const formData = new FormData();
      if (storagePath) {
        formData.append('storage_path', storagePath);
      } else if (emailId) {
        formData.append('email_id', emailId);
      }

      const response = await fetch(`${this.baseURL}/${clientId}/settlement-instructions/get-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      logger.error('Error getting settlement instruction URL:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to get settlement instruction URL'
      };
    }
  }

  /**
   * Get forward expiries for a client on a specific date.
   * Returns trade documents from the trades collection (not the legacy expiries sub-collection).
   */
  async getForwardExpiries(clientId: string, date: string): Promise<APIResponse<Record<string, any>[]>> {
    try {
      logger.log(`[ClientService] Fetching forward expiries for client ${clientId} on ${date}`);

      const response = await this.makeRequest<Record<string, any>[]>(
        `${this.baseURL}/${clientId}/forward-expiries?date=${date}`,
        { method: 'GET' }
      );

      logger.log(`[ClientService] Retrieved ${response.data?.length || 0} forward expiries`);
      return response;
    } catch (error) {
      logger.error('Error fetching forward expiries:', error);
      throw error;
    }
  }

  /**
   * Batch-update expiryState on multiple trades (e.g. to 'agreed' or 'disputed').
   */
  async updateExpiryState(
    clientId: string,
    tradeIds: string[],
    state: 'agreed' | 'disputed' | 'notified',
    accountSelections?: Record<string, { cargarAccountId?: string; abonarAccountId?: string }>
  ): Promise<APIResponse<any>> {
    try {
      const payload: any = { trade_ids: tradeIds, state };
      if (accountSelections) payload.account_selections = accountSelections;
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/expiry-state`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      );
    } catch (error) {
      logger.error('Error updating expiry state:', error);
      throw error;
    }
  }

  async getExpiryTolerances(clientId: string): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/settings/expiry-tolerances`,
        { method: 'GET' }
      );
    } catch (error) {
      logger.error('Error fetching expiry tolerances:', error);
      throw error;
    }
  }

  async updateExpiryTolerances(clientId: string, settings: any): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/settings/expiry-tolerances`,
        { method: 'PUT', body: JSON.stringify(settings) }
      );
    } catch (error) {
      logger.error('Error updating expiry tolerances:', error);
      throw error;
    }
  }

  /**
   * Accept counterparty settlement values for disputed expiry trades.
   * Archives our original values and overwrites with counterparty amounts.
   */
  async acceptCounterpartyValues(
    clientId: string,
    tradeIds: string[]
  ): Promise<APIResponse<{ updated: string[] }>> {
    try {
      return await this.makeRequest<{ updated: string[] }>(
        `${this.baseURL}/${clientId}/expiry-accept-counterparty`,
        {
          method: 'POST',
          body: JSON.stringify({ tradeIds }),
        }
      );
    } catch (error) {
      logger.error('Error accepting counterparty values:', error);
      throw error;
    }
  }

  /**
   * Manually accept counterparty-proposed settlement amounts for notified trades.
   * Archives our original values, overwrites with user-supplied amounts, sets expiryState='agreed'.
   */
  async manualAcceptAmounts(
    clientId: string,
    trades: { tradeId: string; wePayAmount?: number; weReceiveAmount?: number }[]
  ): Promise<APIResponse<{ updated: string[] }>> {
    try {
      return await this.makeRequest<{ updated: string[] }>(
        `${this.baseURL}/${clientId}/expiry-manual-accept`,
        {
          method: 'POST',
          body: JSON.stringify({ trades }),
        }
      );
    } catch (error) {
      logger.error('Error manual-accepting amounts:', error);
      throw error;
    }
  }

  /**
   * Get all counterparty expiries for a client (counterparty's view of expiring trades)
   */
  async getCounterpartyExpiries(clientId: string): Promise<APIResponse<CounterpartyExpiry[]>> {
    try {
      logger.log(`[ClientService] Fetching counterparty expiries for client ${clientId}`);

      const response = await this.makeRequest<CounterpartyExpiry[]>(
        `${this.baseURL}/${clientId}/counterparty-expiries`,
        { method: 'GET' }
      );

      logger.log(`[ClientService] Retrieved ${response.data?.length || 0} counterparty expiries`);
      return response;
    } catch (error) {
      logger.error('Error fetching counterparty expiries:', error);
      throw error;
    }
  }

  /**
   * Get confirmation data for a matched trade
   * Returns the email/PDF confirmation and extracted fields for comparison
   */
  async getTradeConfirmation(clientId: string, tradeId: string): Promise<any> {
    try {
      logger.log(`[ClientService] Fetching confirmation for trade ${tradeId}`);

      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/trades/${tradeId}/confirmation`,
        { method: 'GET' }
      );

      logger.log(`[ClientService] Retrieved confirmation data for trade ${tradeId}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching trade confirmation:', error);
      throw error;
    }
  }

  /**
   * Get confirmation exceptions (unrecognised confirmations and duplicates)
   */
  async getConfirmationExceptions(clientId: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Fetching confirmation exceptions for client ${clientId}`);

      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/confirmation-exceptions`,
        { method: 'GET' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to fetch confirmation exceptions:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to fetch exceptions',
      };
    }
  }

  /**
   * Acknowledge a duplicate confirmation.
   * KAN-178: If tradeIndex is provided, acknowledges only that specific trade.
   */
  async acknowledgeDuplicate(clientId: string, emailId: string, tradeIndex?: number): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Acknowledging duplicate email ${emailId}${tradeIndex !== undefined ? ` trade #${tradeIndex}` : ''}`);
      const qs = tradeIndex !== undefined ? `?trade_index=${tradeIndex}` : '';
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/acknowledge-duplicate/${emailId}${qs}`,
        { method: 'POST' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to acknowledge duplicate:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to acknowledge duplicate',
      };
    }
  }

  /**
   * Reprocess an unrecognised confirmation email to attempt matching again.
   * KAN-178: If tradeIndex is provided, reprocesses only that specific trade.
   */
  async reprocessEmail(clientId: string, emailId: string, tradeIndex?: number): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Reprocessing email ${emailId}${tradeIndex !== undefined ? ` trade #${tradeIndex}` : ''}`);
      const qs = tradeIndex !== undefined ? `?trade_index=${tradeIndex}` : '';
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/reprocess-email/${emailId}${qs}`,
        { method: 'POST' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to reprocess email:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to reprocess email',
      };
    }
  }

  /**
   * Dismiss an unrecognised confirmation (e.g., received in error).
   * KAN-178: If tradeIndex is provided, dismisses only that specific trade.
   */
  async dismissEmail(clientId: string, emailId: string, tradeIndex?: number, bankTradeNumber?: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Dismissing email ${emailId}${tradeIndex !== undefined ? ` trade #${tradeIndex}` : ''}${bankTradeNumber ? ` BTN=${bankTradeNumber}` : ''}`);
      const params = new URLSearchParams();
      if (tradeIndex !== undefined) params.set('trade_index', String(tradeIndex));
      if (bankTradeNumber) params.set('bank_trade_number', bankTradeNumber);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/dismiss-email/${emailId}${qs}`,
        { method: 'POST' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to dismiss email:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to dismiss email',
      };
    }
  }

  // ========== Manual Matching & Unmatch ==========

  async manualMatch(clientId: string, emailId: string, tradeId: string, tradeIndex?: number, bankTradeNumber?: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Manual matching email ${emailId} to trade ${tradeId}${tradeIndex !== undefined ? ` (trade #${tradeIndex})` : ''}${bankTradeNumber ? ` BTN=${bankTradeNumber}` : ''}`);
      const params = new URLSearchParams();
      if (tradeIndex !== undefined) params.set('trade_index', String(tradeIndex));
      if (bankTradeNumber) params.set('bank_trade_number', bankTradeNumber);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/manual-match/${emailId}/${tradeId}${qs}`,
        { method: 'POST' }
      );
      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to create manual match:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to create manual match',
      };
    }
  }

  async unmatchTrade(clientId: string, tradeId: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Unmatching trade ${tradeId}`);
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/unmatch-trade/${tradeId}`,
        { method: 'POST' }
      );
      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to unmatch trade:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to unmatch trade',
      };
    }
  }

  // ========== Find Confirmation (KAN-205a) ==========

  /**
   * Search all confirmation emails by BTN, date range, and/or status.
   * At least one of btn, dateFrom, or dateTo must be provided.
   * Used by the "Find Confirmation" panel in the exceptions modal.
   */
  async searchConfirmationEmails(
    clientId: string,
    params: {
      btn?: string;
      subject?: string;
      sender?: string;
      dateFrom?: string;
      dateTo?: string;
      timeFrom?: string;
      timeTo?: string;
    }
  ): Promise<APIResponse<EmailSearchResult[]>> {
    try {
      const qs = new URLSearchParams();
      if (params.btn) qs.set('btn', params.btn);
      if (params.subject) qs.set('subject', params.subject);
      if (params.sender) qs.set('sender', params.sender);
      if (params.dateFrom) qs.set('date_from', params.dateFrom);
      if (params.dateTo) qs.set('date_to', params.dateTo);
      if (params.timeFrom) qs.set('time_from', params.timeFrom);
      if (params.timeTo) qs.set('time_to', params.timeTo);
      return await this.makeRequest<EmailSearchResult[]>(
        `${this.baseURL}/${clientId}/emails/search?${qs.toString()}`
      );
    } catch (error) {
      logger.error('[ClientService] Failed to search confirmation emails:', error);
      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : 'Failed to search emails',
      };
    }
  }

  /**
   * Release a matched confirmation email back to the exceptions tray.
   * Undoes the match and puts the email into the unrecognised queue.
   * For correctionHistory emails the trade stays matched; for simple matches
   * the trade is reset to unmatched (blocked if ContractSigned/Final).
   */
  async releaseEmailToExceptions(clientId: string, emailId: string, emailTradeIndex?: number): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Releasing email ${emailId} trade index ${emailTradeIndex ?? 'all'} to exceptions`);
      const url = emailTradeIndex != null
        ? `${this.baseURL}/${clientId}/emails/${emailId}/release?email_trade_index=${emailTradeIndex}`
        : `${this.baseURL}/${clientId}/emails/${emailId}/release`;
      return await this.makeRequest<any>(url, { method: 'POST' });
    } catch (error) {
      logger.error('[ClientService] Failed to release email to exceptions:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to release confirmation',
      };
    }
  }

  async acceptCounterpartyValue(
    clientId: string,
    tradeId: string,
    fieldName: string,
    matchId: string,
  ): Promise<APIResponse<{
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    allDiscrepanciesResolved: boolean;
    newConfirmationStatus: string;
  }>> {
    try {
      logger.log(`[ClientService] Accepting counterparty value for ${fieldName} on trade ${tradeId}`);
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/trades/${tradeId}/accept-counterparty-value`,
        {
          method: 'PATCH',
          body: JSON.stringify({ fieldName, matchId }),
        }
      );
      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to accept counterparty value:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to accept counterparty value',
      };
    }
  }

  async acceptSettlementTypeCascade(
    clientId: string,
    tradeId: string,
    matchId: string,
    cascadeFields: Array<{ fieldName: string; value: string | null }>,
  ): Promise<APIResponse<{
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    allDiscrepanciesResolved: boolean;
    newConfirmationStatus: string;
    cascadeFieldsUpdated: string[];
  }>> {
    try {
      logger.log(`[ClientService] Accepting settlement type cascade on trade ${tradeId}`);
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/trades/${tradeId}/accept-settlement-type-cascade`,
        {
          method: 'PATCH',
          body: JSON.stringify({ matchId, cascadeFields }),
        }
      );
      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to accept settlement type cascade:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to accept settlement type cascade',
      };
    }
  }

  async acceptPriceCascade(
    clientId: string,
    tradeId: string,
    matchId: string,
    spotPrice: number,
    forwardPoints: number,
  ): Promise<APIResponse<{
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    allDiscrepanciesResolved: boolean;
    newConfirmationStatus: string;
  }>> {
    try {
      logger.log(`[ClientService] Accepting price cascade on trade ${tradeId}`);
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/trades/${tradeId}/accept-price-cascade`,
        {
          method: 'PATCH',
          body: JSON.stringify({ matchId, spotPrice, forwardPoints }),
        }
      );
      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to accept price cascade:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to accept price cascade',
      };
    }
  }

  async retryProcessing(clientId: string, emailId: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Retrying processing for email ${emailId}`);
      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/retry-processing/${emailId}`,
        { method: 'POST' }
      );
      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to retry processing:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to retry processing',
      };
    }
  }

  // ========== Confirmation Comparison Fields Settings ==========

  /**
   * Get confirmation comparison fields configuration for a client
   */
  async getConfirmationComparisonFields(clientId: string): Promise<APIResponse<ConfirmationComparisonFieldsSettings>> {
    try {
      logger.log(`[ClientService] Fetching confirmation comparison fields for client ${clientId}`);

      const response = await this.makeRequest<ConfirmationComparisonFieldsSettings>(
        `${this.baseURL}/${clientId}/settings/confirmation-comparison-fields`,
        { method: 'GET' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to get confirmation comparison fields:', error);
      return {
        success: false,
        data: null as unknown as ConfirmationComparisonFieldsSettings,
        message: error instanceof Error ? error.message : 'Failed to get confirmation comparison fields',
      };
    }
  }

  /**
   * Update confirmation comparison fields configuration for a client
   */
  async updateConfirmationComparisonFields(
    clientId: string,
    settings: { counterpartyFields: Record<string, CounterpartyComparisonFields> }
  ): Promise<APIResponse<ConfirmationComparisonFieldsSettings>> {
    try {
      logger.log(`[ClientService] Updating confirmation comparison fields for client ${clientId}`);

      const response = await this.makeRequest<ConfirmationComparisonFieldsSettings>(
        `${this.baseURL}/${clientId}/settings/confirmation-comparison-fields`,
        {
          method: 'PUT',
          body: JSON.stringify(settings)
        }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to update confirmation comparison fields:', error);
      return {
        success: false,
        data: null as unknown as ConfirmationComparisonFieldsSettings,
        message: error instanceof Error ? error.message : 'Failed to update confirmation comparison fields',
      };
    }
  }

  // ========== FXGO Corrections ==========

  /**
   * Get pending FXGO corrections awaiting user review
   */
  async getFxgoPendingCorrections(clientId: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Fetching FXGO pending corrections for client ${clientId}`);

      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/fxgo-pending-corrections`,
        { method: 'GET' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to fetch FXGO pending corrections:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to fetch FXGO corrections',
      };
    }
  }

  /**
   * Accept a pending FXGO correction (apply corrected fields to the trade)
   */
  async acceptFxgoCorrection(clientId: string, correctionId: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Accepting FXGO correction ${correctionId} for client ${clientId}`);

      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/fxgo-pending-corrections/${correctionId}/accept`,
        { method: 'POST' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to accept FXGO correction:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to accept FXGO correction',
      };
    }
  }

  /**
   * Dismiss a pending FXGO correction without applying changes
   */
  async dismissFxgoCorrection(clientId: string, correctionId: string): Promise<APIResponse<any>> {
    try {
      logger.log(`[ClientService] Dismissing FXGO correction ${correctionId} for client ${clientId}`);

      const response = await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/fxgo-pending-corrections/${correctionId}/dismiss`,
        { method: 'POST' }
      );

      return response;
    } catch (error) {
      logger.error('[ClientService] Failed to dismiss FXGO correction:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to dismiss FXGO correction',
      };
    }
  }

  // ========== Diverted Emails (KAN-225) ==========

  async getDivertedEmails(clientId: string): Promise<APIResponse<any[]>> {
    try {
      return await this.makeRequest<any[]>(
        `${this.baseURL}/${clientId}/diverted-emails`
      );
    } catch (error) {
      logger.error('[ClientService] Failed to fetch diverted emails:', error);
      return {
        success: false,
        data: [],
        message: error instanceof Error ? error.message : 'Failed to fetch diverted emails',
      };
    }
  }

  // ========== Mass Contract Upload (KAN-238) ==========

  /**
   * Get trades eligible for mass contract upload
   */
  async getMassUploadEligibleTrades(
    clientId: string,
    counterpartyId: string,
    valueDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      counterparty_id: counterpartyId,
      value_date: valueDate,
    });
    const response = await this.makeRequest<any[]>(
      `${this.baseURL}/${clientId}/contracts/mass-upload/eligible-trades?${params}`
    );
    return response.data;
  }

  /**
   * Upload multiple contract PDFs to temp storage and run BTN matching
   */
  async massUploadContracts(
    clientId: string,
    files: File[],
    tradeIds: string[],
    tradeEntityMap?: Record<string, string>,
  ): Promise<any> {
    const token = await this.getAuthToken();

    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('trade_ids', JSON.stringify(tradeIds));
    if (tradeEntityMap && Object.keys(tradeEntityMap).length > 0) {
      formData.append('trade_entity_map', JSON.stringify(tradeEntityMap));
    }

    const response = await fetch(
      `${this.baseURL}/${clientId}/contracts/mass-upload`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || error.detail || 'Failed to upload contracts');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Process matched contract-trade pairs (upload and optionally analyse)
   */
  async massProcessContracts(
    clientId: string,
    pairs: Array<{ trade_id: string; temp_pdf_path: string; filename: string; entity_id?: string }>,
    analyse: boolean,
    sessionId: string,
  ): Promise<any> {
    const response = await this.makeRequest<any>(
      `${this.baseURL}/${clientId}/contracts/mass-process`,
      {
        method: 'POST',
        body: JSON.stringify({ pairs, analyse, session_id: sessionId }),
      }
    );
    return response.data;
  }

  async dismissDivertedEmail(clientId: string, messageId: string, emailTradeIndex?: number): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/dismiss`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailTradeIndex }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to dismiss diverted email:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to dismiss diverted email',
      };
    }
  }

  async getPendingModificationTrades(clientId: string, bankName?: string): Promise<APIResponse<any[]>> {
    try {
      const url = bankName
        ? `${this.baseURL}/${clientId}/pending-modification-trades?bank_name=${encodeURIComponent(bankName)}`
        : `${this.baseURL}/${clientId}/pending-modification-trades`;
      return await this.makeRequest<any[]>(url);
    } catch (error) {
      logger.error('[ClientService] Failed to fetch pending modification trades:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to fetch pending modification trades',
      };
    }
  }

  async applyModificationMatch(
    clientId: string,
    messageId: string,
    tradeId: string,
    entityId: string,
    emailTradeIndex?: number
  ): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/apply-match`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tradeId, entityId, emailTradeIndex }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to apply modification match:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to apply modification match',
      };
    }
  }

  /**
   * Get a signed URL for a diverted email's PDF attachment.
   * Signed URLs are valid for 1 hour.
   */
  async getDivertedEmailPdfUrl(clientId: string, messageId: string, attachmentIndex?: number): Promise<string | null> {
    try {
      const url = attachmentIndex != null
        ? `${this.baseURL}/${clientId}/diverted-emails/${messageId}/pdf-url?attachment_index=${attachmentIndex}`
        : `${this.baseURL}/${clientId}/diverted-emails/${messageId}/pdf-url`;
      const result = await this.makeRequest<any>(url);
      return result.data?.signed_url || null;
    } catch (error) {
      logger.error('[ClientService] Failed to get diverted email PDF URL:', error);
      return null;
    }
  }

  /**
   * Run LLM validation on a diverted modification email against a PendingModification trade.
   * Returns field comparisons and extracted BTN — does NOT apply changes.
   */
  async validateModificationMatch(
    clientId: string,
    messageId: string,
    tradeId: string,
    entityId: string,
    processingMode: 'confirmation' | 'contract',
    emailTradeIndex?: number,
    sourceBankTradeNumber?: string
  ): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/validate-modification-match`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tradeId, entityId, processingMode, emailTradeIndex, sourceBankTradeNumber }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to validate modification match:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to validate modification match',
      };
    }
  }

  /**
   * Confirm a modification match after LLM validation.
   * Stores BTN, optionally applies override fields, restores trade to Confirmed.
   */
  async confirmModificationMatch(
    clientId: string,
    messageId: string,
    tradeId: string,
    entityId: string,
    btn: string | null,
    overrideFields?: Record<string, any>,
    emailTradeIndex?: number
  ): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/confirm-modification-match`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tradeId, entityId, btn, overrideFields, emailTradeIndex }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to confirm modification match:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to confirm modification match',
      };
    }
  }

  /**
   * Reject a modification match — trade stays in PendingModification,
   * diverted email marked as 'rejected'.
   */
  async rejectModificationMatch(
    clientId: string,
    messageId: string,
    tradeId: string,
    entityId: string,
    reason?: string,
    emailTradeIndex?: number
  ): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/reject-modification-match`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tradeId, entityId, reason, emailTradeIndex }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to reject modification match:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to reject modification match',
      };
    }
  }

  /**
   * Create a modification from a counterparty email (Path 3).
   * Runs LLM extraction and either auto-applies (no discrepancies) or returns
   * comparisons for the override UI.
   */
  async createModificationFromEmail(
    clientId: string,
    messageId: string,
    tradeId: string,
    entityId: string,
    modificationType: string,
    processingMode: 'confirmation' | 'contract',
    emailTradeIndex?: number,
    sourceBankTradeNumber?: string
  ): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/create-modification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tradeId, entityId, modificationType, processingMode, emailTradeIndex, sourceBankTradeNumber }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to create modification from email:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to create modification from email',
      };
    }
  }

  /**
   * Confirm a counterparty-initiated modification after reviewing discrepancies.
   */
  async confirmCreateModification(
    clientId: string,
    messageId: string,
    tradeId: string,
    entityId: string,
    modificationType: string,
    btn?: string,
    overrideFields?: Record<string, any>,
    emailTradeIndex?: number
  ): Promise<APIResponse<any>> {
    try {
      return await this.makeRequest<any>(
        `${this.baseURL}/${clientId}/diverted-emails/${messageId}/confirm-create-modification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tradeId, entityId, modificationType, btn, overrideFields, emailTradeIndex }),
        }
      );
    } catch (error) {
      logger.error('[ClientService] Failed to confirm create modification:', error);
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to confirm create modification',
      };
    }
  }
}

// Export singleton instance
export const clientService = new ClientService();
// Export class for static methods
export { ClientService };
export default clientService;